/**
 * SharedAnalysisPage — public view-only display of a frozen share snapshot.
 * Outside GoogleAuthGate. No save / edit / file workflow.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DealCurrencyProvider } from '@/context/DealCurrencyContext';
import { KPIHeader } from '@/components/KPIHeader';
import { InputPanel } from '@/components/InputPanel';
import { OutputPanel } from '@/components/OutputPanel';
import { DecisionCockpit } from '@/components/DecisionCockpit';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { API_BASE_URL } from '@/config/api';
import { validateInputs } from '@/types/dealTypes';
import { fetchShareSnapshot } from '@/utils/shareApi';
import {
  buildCombinedVerdict,
  getAssessmentForScenario,
  hydrateLandAssessmentStore,
} from '@/utils/landAssessment';

const API = API_BASE_URL;

const EMPTY_OUTPUTS = {
  grossBuildableArea: 0, netSaleableArea: 0, numberOfUnits: 0, unitsAutoCalculated: false,
  avgSqmPerUnit: 0, pricePerUnitWithoutParking: 0, pricePerUnitWithParking: 0,
  pricePerUnitMin: 0, pricePerUnitMax: 0, parkingsPerUnit: 0, parkingValuePerUnit: 0,
  pricePositioning: 'market', priceDelta: 0,
  salesRevenue: 0, parkingRevenue: 0, totalRevenue: 0,
  landCost: 0, notaryFees: 0, notaryFeesRate: 0, otherLandFees: 0, otherLandFeesRate: 0, totalLandCost: 0,
  constructionCost: 0, constructionRate: 0, constructionReserve: 0, constructionReserveRate: 0, totalConstructionCost: 0,
  architectureCost: 0, architectureRate: 0, consultantsCost: 0, consultantsRate: 0,
  contingencyCost: 0, contingencyRate: 0, softCosts: 0,
  commercializationCost: 0, commercializationRate: 0, courtageCost: 0, courtageRate: 0,
  totalDevCost: 0, profitBeforeTax: 0, taxAmount: 0, taxRate: 0, profitAfterTax: 0,
  netAfterFinancing: 0, profitShareAmount: 0, finalNetProfit: 0,
  equityRequired: 0, equityRate: 0, equityBase: 0, equityBaseSource: '',
  bankDebtAmount: 0, bankDebtRate: 0, bankDebtBase: 0, bankDebtBaseSource: '', constructionFacilityAmount: 0,
  privateCapitalAmount: 0, privateCapitalRate: 0, privateCapitalBase: 0, privateCapitalBaseSource: '', privateCapitalType: 'debt',
  fundingTotal: 0, fundingCoverageRatio: 0,
  upfrontFees: 0, feesRate: 0, feesBase: 0, feesBaseSource: '',
  feasibilityStudy: 0, capitalInvested: 0,
  bankInterestCost: 0, bankSetupCost: 0, privateDebtCost: 0, privateDebtFees: 0,
  equityStructuringCost: 0, totalFinancingCost: 0,
  financingLabel: 'Equity only', bankDurationMonths: 18, bankInterestRate: 0.02, pcProfitShare: 0,
  totalCost: 0, roiOnEquity: 0, marginPercent: 0, landToRevenueRatio: 0,
  pricePerSqmEffective: 0, costPerSqm: 0,
};

async function fetchCalculation(inputs, assumptions) {
  try {
    const { address, comment, locationData, parkingSpotsManual, ...calcInputs } = inputs;
    const body = { inputs: calcInputs, assumptions };
    if (assumptions?.fxSnapshot) {
      body.assumptions = {
        ...assumptions,
        fxMode: 'frozen',
        fxSnapshot: assumptions.fxSnapshot,
      };
    }
    // Prefer share fxSnapshot over assumptions if present at top-level hydrate
    const res = await fetch(`${API}/api/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inputs: calcInputs, assumptions: body.assumptions || assumptions }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function SharedAnalysisPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [message, setMessage] = useState('');
  const [share, setShare] = useState(null);
  const [outputs, setOutputs] = useState(EMPTY_OUTPUTS);
  const [outputsChf, setOutputsChf] = useState({});
  const [decision, setDecision] = useState({ verdict: '…', reason: 'Loading…', warnings: [] });
  const [qualityGrade, setQualityGrade] = useState({});
  const [fx, setFx] = useState({ base: 'CHF', quote: 'CHF', rate: 1, source: 'identity', mode: 'live', stale: false });
  const [syncing, setSyncing] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus('loading');
      const result = await fetchShareSnapshot(token);
      if (cancelled) return;
      if (!result.ok) {
        setStatus('error');
        setMessage(result.message || 'Share not found');
        setSyncing(false);
        return;
      }
      setShare(result.data);
      setStatus('ok');
    })();
    return () => { cancelled = true; };
  }, [token]);

  const inputs = useMemo(() => {
    const cleaned = validateInputs(share?.inputs || {});
    // OutputPanel expects these nested objects; older shares may omit them.
    return {
      ...cleaned,
      overrides: cleaned.overrides && typeof cleaned.overrides === 'object' ? cleaned.overrides : {},
      capitalSources:
        cleaned.capitalSources && typeof cleaned.capitalSources === 'object'
          ? cleaned.capitalSources
          : {},
    };
  }, [share]);
  const assumptions = share?.assumptions || {};
  const operatingCurrency = (assumptions.operatingCurrency || 'CHF').toUpperCase();
  const countryCode = (assumptions.countryCode || 'CH').toUpperCase();

  useEffect(() => {
    if (status !== 'ok' || !share) return undefined;
    let cancelled = false;
    setSyncing(true);
    (async () => {
      const frozenAssumptions = {
        ...assumptions,
        ...(share.fxSnapshot
          ? { fxMode: 'frozen', fxSnapshot: share.fxSnapshot }
          : {}),
      };
      const result = await fetchCalculation(inputs, frozenAssumptions);
      if (cancelled) return;
      if (result) {
        setOutputs(result.outputs || EMPTY_OUTPUTS);
        setOutputsChf(result.outputsChf || {});
        setDecision(result.decision || { verdict: '—', reason: '', warnings: [] });
        setQualityGrade(result.qualityGrade || {});
        setFx(result.fx || fx);
      }
      setSyncing(false);
    })();
    return () => { cancelled = true; };
  }, [status, share, inputs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hooks must run before early returns (Rules of Hooks).
  const landAssessmentStore = useMemo(
    () => hydrateLandAssessmentStore(share?.landAssessment),
    [share],
  );
  const activeLandAssessment = useMemo(
    () => getAssessmentForScenario(landAssessmentStore, share?.activeScenarioId),
    [landAssessmentStore, share],
  );
  const combinedVerdict = useMemo(
    () => buildCombinedVerdict({
      qualityGrade,
      decision,
      assessment: activeLandAssessment,
    }),
    [qualityGrade, decision, activeLandAssessment],
  );

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="share-loading">
        <div className="text-[12px] text-slate-500 animate-pulse">Loading shared analysis…</div>
      </div>
    );
  }

  if (status === 'error' || !share) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" data-testid="share-error">
        <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-5 text-center shadow-sm">
          <h1 className="text-sm font-bold text-slate-900 mb-1">Share unavailable</h1>
          <p className="text-[12px] text-slate-500">{message || 'This view-only link is invalid or expired.'}</p>
        </div>
      </div>
    );
  }

  const noop = () => {};

  return (
    <ErrorBoundary>
      <DealCurrencyProvider
        operatingCurrency={operatingCurrency}
        countryCode={countryCode}
        fx={fx}
        outputs={outputs}
        outputsChf={outputsChf}
        taxLabel={assumptions.taxLabel || 'IBGI'}
      >
        <div className="App min-h-screen bg-gray-100" data-testid="share-view-container">
          <div
            className="fixed top-[60px] lg:top-[30px] left-0 right-0 z-[60] bg-indigo-50 border-b border-indigo-200 px-3 py-1.5 flex items-center justify-between gap-2"
            data-testid="share-view-only-banner"
          >
            <span className="text-[10px] text-indigo-800">
              <span className="font-bold uppercase tracking-wide mr-1">View only</span>
              Shared analysis — inputs and settings cannot be changed.
            </span>
            <span className="text-[9px] text-indigo-600 truncate max-w-[40%]">{share.name}</span>
          </div>

          <KPIHeader
            roiOnEquity={outputs.roiOnEquity}
            verdict={combinedVerdict.verdict}
            reason={combinedVerdict.reason}
            landScore={combinedVerdict.landScore}
            currentFileName={share.name || 'Shared analysis'}
            isDraft={false}
            saveFlash={false}
            viewOnly
            analysisTab="land"
          />

          <div
            className="flex flex-col lg:flex-row gap-0 lg:gap-0.5 p-0.5 lg:p-1 max-w-[1920px] mx-auto lg:h-[calc(100vh-44px)] mt-[44px]"
            data-testid="share-main-layout"
          >
            <InputPanel
              inputs={inputs}
              onInputChange={noop}
              operatingCurrency={operatingCurrency}
              readOnly
            />
            <OutputPanel
              outputs={outputs}
              outputsChf={outputsChf}
              fx={fx}
              taxLabel={assumptions.taxLabel || 'IBGI'}
              operatingCurrency={operatingCurrency}
              countryCode={countryCode}
              warnings={decision.warnings}
              inputs={inputs}
              onInputChange={noop}
              notes={share.notes || ''}
              onNotesChange={noop}
              readOnly
            />
            <DecisionCockpit
              visible
              analysisTab="land"
              inputs={inputs}
              onInputChange={noop}
              outputs={outputs}
              outputsChf={outputsChf}
              fx={fx}
              decision={decision}
              qualityGrade={qualityGrade}
              combinedVerdict={combinedVerdict}
              assumptions={assumptions}
              notes={share.notes || ''}
              cadastreData={share.cadastreData || null}
              onCadastreData={noop}
              marketData={share.marketData || null}
              onMarketData={noop}
              readOnly
            />
          </div>
        </div>
      </DealCurrencyProvider>
    </ErrorBoundary>
  );
}
